import {
  getBalance,
  getEnergyLimit,
  getEnergyPerSecond,
  getIsAfter1Day,
  getIsAfter2Days,
  getIsbetween1And2Days,
  getLeague,
  getReferralBonus,
  getRocketmanBonus,
  getTaps,
  isNotExpired,
  getBaseEnergy,
} from './game.getters';
import { MutableState } from '../../schema';
import { Booster, rocketmanConfig } from './ruleset/boosters';
import {
  REFERRAL_LEVEL_UPS_PREMIUM,
  REFERRAL_LEVEL_UPS_REGULAR,
  RESERVED_REFERRAL_ID,
} from './ruleset/referrals';
import { LEVEL_UP_BONUSES } from './ruleset/league';
import {
  ReplicantAsyncActionAPI,
  ReplicantEventHandlerAPI,
  ReplicantSyncActionAPI,
} from '@play-co/replicant';
import { ReplicantServer } from '../../config';
import { TelegramUser } from '../chatbot/chatbot.schema';
import { clampScore } from '../../utils/numbers';
import { DAY_IN_MS, HOUR_IN_MS, MIN_IN_MS, isNextDay } from '../../utils/time';
import {
  EarningKey,
  earningCondition,
  earningRewards,
} from './ruleset/earnings';
import { BoosterPurchaseUserProps } from './ruleset/analytics';
import {
  getGiftsNeededForNextLevel,
  getPowerUpIdsWithConditionKey,
  getPowerUpAvailableTime,
  getPowerUps,
  getTotalDailyBonusReward,
  isDailyPowerUp,
  getMiningRevenues,
} from '../powerups/getters';
import { PowerUpCardType } from '../powerups/types';
import { HIDDEN_CARD_REWARD } from '../powerups/ruleset';
import { getWhitelistedBadgesIds } from './game.getters';
import { ExpectedError } from '../../types';
import { MAX_DAILY_REWARD_STREAK } from './ruleset/dailyRewards';
import { checkQuest, getQuest } from '../quests/getters';
import { ErrorCode, successResponse } from '../../response';

export function updateTelegramProps(
  state: MutableState,
  telegramUser: TelegramUser,
) {
  let username = telegramUser.username;
  if (username === undefined) {
    username = telegramUser.first_name;
    if (telegramUser.last_name) {
      username = `${username} ${telegramUser.last_name}`;
    }
  }

  state.username = username;
  state.is_premium = Boolean(telegramUser.is_premium);
}

export function incrementBalance(state: MutableState, increment: number) {
  state.balance += increment;
  state.league = getLeague(state);
}

export function incrementScore(state: MutableState, increment: number) {
  state.score = clampScore(state.score + increment);
  incrementBalance(state, increment);

  if (state.team_id) {
    state.unsynced_team_score += increment;
  }
}

export function incrementScoreAndTaps(state: MutableState, increment: number) {
  incrementScore(state, increment);
  state.taps += increment;
}

export function incrementTokenCreationCredits(
  state: MutableState,
  increment: number,
) {
  state.tokenCreationCredits += increment;
}

export function resetBuffs(state: MutableState, now: number) {
  state.free_rocketman_used = state.free_rocketman_used.filter((timestamp) =>
    isNotExpired(timestamp, rocketmanConfig.freeCooldown, now),
  );
  state.rocketman_used = state.rocketman_used.filter((timestamp) =>
    isNotExpired(timestamp, DAY_IN_MS, now),
  );
}

export function consumeTaps(
  state: MutableState,
  taps: number,
  api: ReplicantSyncActionAPI<ReplicantServer>,
) {
  if (taps === 0) {
    return;
  }

  const now = api.date.now();
  resetBuffs(state, now);
  refreshEnergy(state, now);

  let energyUsed = getTaps(state) * taps;
  if (state.energy < energyUsed) {
    energyUsed = state.energy;
    if (energyUsed <= 0) {
      return;
    }
  }

  // update score/balance/taps
  incrementScoreAndTaps(state, energyUsed);

  // get rocketman status
  const { multiplier } = getRocketmanBonus(state, now);

  // only use energy when there's no rocketman bonus
  if (multiplier === 1) {
    state.energy -= energyUsed;
  }

  state.session_taps += taps;

  const currentLeague = getLeague(state);
  const nextLeague = getLeague(state);
  if (currentLeague !== nextLeague) {
    const ownBonus = LEVEL_UP_BONUSES[nextLeague];

    incrementScore(state, ownBonus);

    if (state.referrer_id) {
      // give bonus to referrer and notify them
      const friendBonuses = state.is_premium
        ? REFERRAL_LEVEL_UPS_PREMIUM
        : REFERRAL_LEVEL_UPS_REGULAR;
      const friendBonus = friendBonuses[nextLeague];

      const referrerId = state.referrer_id;
      api.postMessage.addReferrerScore(referrerId, {
        bonus: friendBonus,
      });

      state.referrerContribution = state.referrerContribution + friendBonus;

      api.sendAnalyticsEvents([
        {
          eventType: 'Bonus',
          eventProperties: {
            nature: 'ownProgression',
            amount: ownBonus,
          },
        },
        {
          userId: referrerId,
          eventType: 'Bonus',
          eventProperties: {
            nature: 'friendProgression',
            amount: friendBonus,
          },
        },
      ]);
    }
  }
}

export const acquireBooster = (
  state: MutableState,
  // todo: what type is api parameter? thugmaster seems to have
  // todo: a whole ecosystem of defined types, computedProps, etc
  // todo: ReplicantSyncActionAPI<PartialReplicant<wholegamestateastype>>
  api: any,
  boosterProps: {
    booster: Booster;
    level: number;
    price: number;
  },
): void | ExpectedError => {
  const { booster, level, price } = boosterProps;

  try {
    const now = api.date.now();
    if (getBalance(state, now) < price) {
      const errorMessage = 'User does not have enough points to buy this item';
      api.sendAnalyticsEvents([
        {
          eventType: 'AcquireBoosterError',
          eventProperties: {
            booster,
            level,
          },
        },
      ]);
      return {
        expectedError: true,
        errorMessage,
      };
    }

    spendCoins(state, price, now);

    const purchaseUserProps: BoosterPurchaseUserProps = {};

    switch (booster) {
      case 'AutoTap':
        state.has_auto_tap = true;
        state.boosterPurchases.autoTaps += 1;
        purchaseUserProps.purchasedAutoTaps = state.boosterPurchases.autoTaps;
        break;
      case 'MultiTap':
        state.tap_level = level as number;
        state.boosterPurchases.multiTaps += 1;
        purchaseUserProps.purchasedMultiTaps = state.boosterPurchases.multiTaps;
        break;
      case 'RechargeLimit':
        state.energy_limit_level = level as number;
        state.boosterPurchases.rechargeLimits += 1;
        purchaseUserProps.purchasedMultiTaps =
          state.boosterPurchases.rechargeLimits;
        break;
      case 'RechargeSpeed':
        state.energy_recharge_level = level as number;
        state.boosterPurchases.rechargeSpeeds += 1;
        purchaseUserProps.purchasedMultiTaps =
          state.boosterPurchases.rechargeSpeeds;
        break;
    }

    // send analytics events
    api.sendAnalyticsEvents([
      {
        eventType: 'AcquireBooster',
        eventProperties: {
          booster,
          level,
        },
        userProperties: purchaseUserProps,
      },
    ]);
  } catch (e) {
    console.log(`Failed to acquire booster`, e);
    throw e;
  }
};

export function refreshEnergy(state: MutableState, now: number) {
  const elapsedTimeSinceLastRefresh = now - (state.last_energy_refresh || 0);
  const elapsedTimeInSeconds = Math.floor(elapsedTimeSinceLastRefresh / 1000);

  const energyPerSecond = getEnergyPerSecond(state);

  const energyToRefresh = elapsedTimeInSeconds * energyPerSecond;

  const energyLimit = getEnergyLimit(state);

  const energy = state.energy + energyToRefresh;

  // the energy update has to be precise or it can lead to subtle bugs
  if (energy >= energyLimit) {
    state.energy = energyLimit;
    state.last_energy_refresh = now;
  } else {
    if (energy !== state.energy) {
      // @important: only update if values changes
      // otherwise the value of last_energy_refresh could keep shifting forward in time with the energy never increasing
      state.energy = energy;
      state.last_energy_refresh = now;
    }
  }
}

export const handleFirstEntry = async (
  state: MutableState,
  payload: { referrer?: string; telegramUser: TelegramUser },
  api:
    | ReplicantEventHandlerAPI<ReplicantServer>
    | ReplicantAsyncActionAPI<ReplicantServer>,
) => {
  const { referrer, telegramUser } = payload;
  const isPremium = Boolean(telegramUser.is_premium);

  updateTelegramProps(state, telegramUser);

  state.first_interaction = false;

  const hasNeverHadZeroEnergy = state.last_energy_zero === 0;
  const hasTaps = state.taps > 0;
  const isNotFirstTimeUser = hasNeverHadZeroEnergy && hasTaps;
  if (isNotFirstTimeUser) {
    state.last_energy_zero = api.date.now();
  }

  state.badgeControl.lastUpdated = api.date.now();

  // only store this list during the first entry
  // it will be cleaned up after the onSessionEnds
  state.badgeControl.whitelisted = getWhitelistedBadgesIds();

  if (referrer) {
    await handleReferralEntry(
      state,
      {
        referrerId: referrer,
        isPremium,
      },
      api,
    );
  }
};

export const handleReferralEntry = async (
  state: MutableState,
  args: { referrerId: string; isPremium?: boolean; teamId?: string },
  api:
    | ReplicantEventHandlerAPI<ReplicantServer>
    | ReplicantAsyncActionAPI<ReplicantServer>,
) => {
  if (state.referrer_id) {
    // player already has a referrer
    return;
  }

  const referrerId = args.referrerId;
  if (referrerId === api.getUserID()) {
    // making sure a user cannot refer themself
    return;
  }

  if (referrerId === RESERVED_REFERRAL_ID) {
    // ignore reserved referral id
    return;
  }

  // called by the bot when a player joins the game through a referral
  const now = api.date.now();
  const isPremium = args.isPremium || false;
  const referralBonus = getReferralBonus(state, now, isPremium);

  // create a player for the new user with initial score
  incrementScore(state, referralBonus);
  state.referrer_id = referrerId;
  state.referrerContribution += referralBonus;

  // create friendship between the 2 players
  state.friendIds.push(referrerId);

  // send analytics events
  api.sendAnalyticsEvents([
    {
      eventType: 'Bonus',
      eventProperties: { nature: 'joinInvite', amount: referralBonus },
      userProperties: { referrerId, friendCount: 1 },
    },
    {
      userId: referrerId,
      eventType: 'Bonus',
      eventProperties: { nature: 'inviteSuccess', amount: referralBonus },
    },
  ]);

  // add friend to referrer and apply bonus
  api.postMessage.addFriend(referrerId, {
    friendId: api.getUserID(),
    bonus: referralBonus,
  });

  api.scheduledActions.schedule.sendFriendJoinedMessage({
    targetUserId: referrerId,
    args: {
      chatId: parseInt(referrerId),
      friendName: state.username,
      bonus: referralBonus,
    },
    notificationId: `friendsJoined.${referrerId}`,
    delayInMS: MIN_IN_MS * 3,
  });
};

export const addVideoQuestReward = (
  state: MutableState,
  { id }: { id?: string },
  api:
    | ReplicantEventHandlerAPI<ReplicantServer>
    | ReplicantAsyncActionAPI<ReplicantServer>
    | ReplicantSyncActionAPI<ReplicantServer>,
) => {
  const video = state.watchedVideos.find((video) => video.id === id);
  if (
    !id ||
    !video ||
    video.claimed ||
    api.date.now() - video.timestamp < HOUR_IN_MS
  ) {
    return;
  }
  video.claimed = true;
  incrementScore(state, 100_000);
};

export const addEarningReward = (
  state: MutableState,
  { earningKey }: { earningKey: EarningKey },
): boolean | ExpectedError => {
  if (state.earnings[earningKey]) {
    return false;
  }

  const claimError = earningCondition[earningKey](state);

  if (claimError) {
    return {
      expectedError: true,
      errorMessage: claimError,
    };
  }

  incrementScore(state, earningRewards[earningKey]);
  state.earnings[earningKey] = true;
  return true;
};

export const claimPowerUpReward = (state: MutableState, now: number) => {
  const reward = getMiningRevenues(state, now, state.powerUps.last_claimed);

  if (reward <= 0) {
    return 0;
  }

  incrementScoreAndTaps(state, reward);
  state.powerUps.last_claimed = now;

  return reward;
};

export const spendCoins = (
  state: MutableState,
  amount: number,
  now: number,
) => {
  if (amount <= 0) {
    return;
  }
  // update balance and avoids the mining reward appearing too large on session start
  claimPowerUpReward(state, now);

  state.balance -= amount;
};

export const initForRequireMoreFriendsFromLast = (
  state: MutableState,
): void => {
  updateAllBasisForRequireMoreFriendsFromLast(state);
};

export const updateAllBasisForRequireMoreFriendsFromLast = (
  state: MutableState,
  opts?: { skipBaseLevelCheck?: boolean },
): void => {
  const powerUpIds = getPowerUpIdsWithConditionKey(
    'requireMoreFriendsFromLast',
  );
  updateBasisForRequireMoreFriendsFromLast(state, {
    powerUpIds,
    skipBaseLevelCheck: opts?.skipBaseLevelCheck,
  });
};

export const updateBasisForRequireMoreFriendsFromLast = (
  state: MutableState,
  {
    powerUpIds,
    skipBaseLevelCheck = false,
  }: { powerUpIds: string[]; skipBaseLevelCheck?: boolean },
): void => {
  for (const powerUpId of powerUpIds) {
    const myPowerUp = state.powerUps.owned[powerUpId];

    const myPowerUpLevel = myPowerUp?.level || 0;
    const baseLevel = state.powerUps.specialsFriendBasis[powerUpId]?.level;
    if (
      !skipBaseLevelCheck &&
      baseLevel !== undefined &&
      myPowerUpLevel === baseLevel
    ) {
      // already sync'd for this power up level, so no need to update
      continue;
    }

    state.powerUps.specialsFriendBasis[powerUpId] = {
      friendCount: state.friendIds.length,
      level: myPowerUp?.level || 0,
    };
  }
};

export const buyPowerUp: (
  state: MutableState,
  now: number,
  payload: { id: string; isFree?: boolean; findAvailGiftIfEnded?: boolean },
  api: ReplicantAsyncActionAPI<ReplicantServer>,
) => Promise<
  | { powerUpId: string; originalPowerUpId: string; isDaily: boolean }
  | ExpectedError
> = async (state, now, payload, api) => {
  const { id, isFree = false } = payload;
  // update balance and avoids the mining reward appearing too large on session start
  claimPowerUpReward(state, now);

  const powerUps = getPowerUps(state, now);

  let powerUp = powerUps.find((pu) => pu.id === id);
  let isDaily = false;
  const isGift = powerUp?.isGift;

  if (!powerUp) {
    throw new Error(`Could not find power up '${id}'`);
  }

  if (!isFree && powerUp.cost > state.balance) {
    return {
      expectedError: true,
      errorMessage: `Not enough funds.`,
    };
  }

  if (powerUp.maxLevel !== undefined && powerUp.level >= powerUp.maxLevel) {
    return {
      expectedError: true,
      errorMessage: `Max level reached.`,
    };
  }

  const isExpired = powerUp.expireTime && now >= powerUp.expireTime;
  if (isExpired && !isGift) {
    return {
      expectedError: true,
      errorMessage: `Expired.`,
    };
  }

  const isEnded = powerUp.endTime && now >= powerUp.endTime;
  const isNotOwned = !state.powerUps.owned[id];
  if (isEnded && isNotOwned && !isGift) {
    return {
      expectedError: true,
      errorMessage: `Ended.`,
    };
  }

  const isEndedOrExpired = isEnded || isExpired;
  if (payload.findAvailGiftIfEnded && isEndedOrExpired && isGift) {
    // find an available gift that is not ended yet
    // sort by endTime furthest out
    powerUp = powerUps
      .filter(
        (item) =>
          item.isGift === true &&
          item.endTime !== undefined &&
          now < item.endTime,
      )
      .sort((a, b) => (b.endTime ?? 0) - (a.endTime ?? 0))[0];
    if (!powerUp) {
      return {
        expectedError: true,
        errorMessage: `Could not find any non expired or ended power up.`,
      };
    }
  }

  if (getPowerUpAvailableTime(state, powerUp) > now) {
    return {
      expectedError: true,
      errorMessage: `Too early to upgrade power-up: ${powerUp.id}`,
    };
  }

  // @TODO: add safety checks for buying specials make sure they are available (or owned?)
  if (
    isDailyPowerUp(powerUp.id, now) &&
    !state.powerUps.daily.power_ups.includes(powerUp.id)
  ) {
    state.powerUps.daily.power_ups.push(id);
    const reward = getTotalDailyBonusReward(state);
    isDaily = true;
    incrementScore(state, reward);
  }

  if (!isFree) {
    spendCoins(state, powerUp.cost, now);
  }

  const isFirstPowerUp = Object.keys(state.powerUps.owned).length === 0;

  // Make sure we set last_claimed to now when purchasing the first card so we reset the elapsed time (that starts in 0)
  if (isFirstPowerUp) {
    state.powerUps.last_claimed = now;
  }

  const isGiftOnlyCard = powerUp.type === PowerUpCardType.GIFT_ONLY;
  if (!state.powerUps.owned[powerUp.id]) {
    state.powerUps.owned[powerUp.id] = {
      level: 0,
    };
  }

  const isSpecial = powerUp.specialState !== undefined;
  if (isSpecial) {
    state.powerUps.owned[powerUp.id].lastClaimed = now;

    const isHidden = powerUp.type === PowerUpCardType.HIDDEN;
    if (isHidden) {
      incrementScore(state, HIDDEN_CARD_REWARD);
    }
  }

  if (isGiftOnlyCard) {
    let currentTotalReceivedGifts =
      state.powerUps.owned[powerUp.id]?.giftsReceived || 0;
    currentTotalReceivedGifts += 1;
    state.powerUps.owned[powerUp.id].giftsReceived = currentTotalReceivedGifts;

    // increment level if user has accumulated enough gifts
    const giftsNeededForNextLevel = getGiftsNeededForNextLevel(powerUp);
    if (currentTotalReceivedGifts === giftsNeededForNextLevel) {
      // increment level
      powerUp.level += 1;
      state.powerUps.owned[powerUp.id].level += 1;
    }
  } else {
    // increment level
    powerUp.level += 1;
    state.powerUps.owned[powerUp.id].level = powerUp.level;
  }

  // only update basis if it's one of the friend-gated powerups
  const powerUpIdsWithRmffl = getPowerUpIdsWithConditionKey(
    'requireMoreFriendsFromLast',
  );
  if (powerUpIdsWithRmffl.includes(powerUp.id)) {
    // update basis for all powerUps so all the other powerUps will require more friends again
    updateBasisForRequireMoreFriendsFromLast(state, {
      powerUpIds: powerUpIdsWithRmffl,
      skipBaseLevelCheck: true,
    });
  }

  clearConditionDiscount(state, powerUp.id);

  return {
    originalPowerUpId: id,
    powerUpId: powerUp.id,
    isDaily,
  };
};

export const clearDailyConditions = (state: MutableState) => {
  const giftDailyWithDiscountMap =
    state.powerUps.conditions.gift_daily_with_discount;
  Object.keys(giftDailyWithDiscountMap).forEach((key) => {
    giftDailyWithDiscountMap[key].dailyGifts = [];
  });

  const giftOnlyMap = state.powerUps.conditions.gift_only;
  Object.keys(giftOnlyMap).forEach((key) => {
    giftOnlyMap[key].dailyGifts = [];
  });
};

export const clearConditionDiscount = (state: MutableState, puId: string) => {
  try {
    state.powerUps.conditions.gift_daily_with_discount[puId].discountList = [];
  } catch {
    // fail silently
  }
};

const tutorialStateMutators: Record<
  string,
  (state: MutableState, opts?: { now: number; test?: boolean }) => void
> = {
  rechargeEnergy: (state) => {
    state.energy = getBaseEnergy(state);
  },
  rechargeRocketman: (state) => {
    state.free_rocketman_used = [];
  },
  startEnergy: (state, opts) => {
    state.energy = 75;
    if (opts?.now) {
      state.last_energy_refresh = opts?.now;
    }
    if (opts?.test) {
      incrementBalance(state, 500);
      state.powerUps.owned = {};
    }
  },
  '1000FreeGift': (state) => {
    if (state.balance <= 3_000) {
      incrementBalance(state, 1_000);
    }
  },
  '1500FreeGift': (state) => {
    if (state.balance <= 4_000) {
      incrementBalance(state, 1_500);
    }
  },
  '100FreeGift': (state) => {
    incrementBalance(state, 100);
  },
  '50Energy': (state) => {
    if (state.energy > 50) {
      state.energy = 50;
    }
  },
};

export const handleTutorialUpdate = (
  state: MutableState,
  {
    tutorialId,
    stepId,
    stepIndex,
    test,
  }: { tutorialId: string; stepId?: string; stepIndex: number; test: boolean },
  now: number,
) => {
  if (stepId) {
    tutorialStateMutators[stepId] &&
      tutorialStateMutators[stepId](state, { now, test });
  }
  state.tutorials[tutorialId] = stepIndex;
};

export const resetDailyCode = (state: MutableState, now: number) => {
  if (isNextDay(state.dailyCode.timestamp, now, 'pst')) {
    state.dailyCode = {
      timestamp: now,
      complete: false,
    };
  }
};

export const handleDailyRewards = (
  state: MutableState,
  now: number,
  api: ReplicantSyncActionAPI<ReplicantServer>,
) => {
  // more than 1d, less than 2
  const hadGrantedRewardBetweenDayOrTwo = getIsbetween1And2Days(
    now,
    state.last_reward_granted,
  );
  // first day after broken streak
  const isFirstConsecutiveDay =
    !state.streak_days && getIsAfter1Day(now, state.last_reward_granted);

  // grant reward if any of the conditions are met
  // any first day user get 500, for the streak rewards are growing
  const grantStreakReward =
    hadGrantedRewardBetweenDayOrTwo || isFirstConsecutiveDay;

  if (grantStreakReward) {
    state.consecutive_days = +1;
    state.last_reward_granted = now;
    // user is on second loop of his streak
    if (state.streak_days === MAX_DAILY_REWARD_STREAK) {
      if (state.unclaimed_rewards) {
        // if he has unclaimed rewards keep him on the same streak
        state.streak_days = Math.min(
          state.streak_days + 1,
          MAX_DAILY_REWARD_STREAK,
        );
      } else {
        state.streak_days = 1;
        state.unclaimed_rewards = 1;
      }
    } else {
      state.streak_days = Math.min(
        state.streak_days + 1,
        MAX_DAILY_REWARD_STREAK,
      );
      state.unclaimed_rewards += 1;
    }
  } else if (getIsAfter2Days(now, state.last_reward_granted)) {
    // user was not granted reward for 48h, broken streak
    // logic is a bit complicated here: grant reward once per 24h
    // means that in fact user can login every 47h 59min and still get the reward
    state.streak_days = 1;
    state.last_reward_granted = now;
    state.unclaimed_rewards = 1;
    api.sendAnalyticsEvents([
      {
        eventType: 'reward_forfeited',
        eventProperties: {
          // use +1 here becase the consecutive_days is 0-indexed
          max_day_returned: state.consecutive_days + 1,
        },
      },
    ]);
    state.consecutive_days = 1;
  }
};

export const updateQuest = (
  state: MutableState,
  now: number,
  { questId }: { questId: string },
) => {
  if (!state.quests[questId]) {
    state.quests[questId] = { state: 'default' };
  }

  if (state.quests[questId].state === 'complete') {
    return;
  }
  const quest = getQuest(questId);
  if (!quest) {
    return;
  }

  const response = checkQuest(state, now, {
    id: quest.id,
    category: quest.category,
    actionCTA: quest.actionCTA,
  });

  if (response.error) {
    const isWaitingForQuest =
      response.code === ErrorCode.START_WAITING_FOR_QUEST;
    const isStateNotWaiting = state.quests[questId].state != 'waiting';

    if (isWaitingForQuest && isStateNotWaiting) {
      state.quests[questId] = {
        state: 'waiting',
        firstCheckAt: now,
      };
    }

    return response;
  } else if (response.data) {
    incrementScore(state, quest.reward);
    state.quests[questId].state = 'complete';

    // @TODO: This fixes the mine quest which requires earning. This should be a temp solution;
    // Mine card system has to be updated to work with latest quest system
    if (questId === 'follow_x') {
      state.earnings.followOnX = true;
    }
  }
  return successResponse(state.quests[questId].state);
};
